import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Filter, X, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

interface SavedPreset {
  id: string;
  name: string;
  filters: any[];
  sorts: any[];
}

interface FilterPresetsProps {
  filters: any[];
  sorts: any[];
  onApplyPreset: (filters: any[], sorts: any[]) => void;
  viewId?: string;
}

export const FilterPresets = ({ filters, sorts, onApplyPreset, viewId }: FilterPresetsProps) => {
  const [presets, setPresets] = useState<SavedPreset[]>([]);
  const [presetName, setPresetName] = useState("");
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  const savePreset = () => {
    if (!presetName.trim()) {
      toast.error("Please enter a preset name");
      return;
    }

    const newPreset: SavedPreset = {
      id: crypto.randomUUID(),
      name: presetName,
      filters: [...filters],
      sorts: [...sorts]
    };

    setPresets([...presets, newPreset]);
    setPresetName("");
    setSaveDialogOpen(false);
    toast.success(`Preset "${newPreset.name}" saved!`);
  };

  const applyPreset = (preset: SavedPreset) => {
    onApplyPreset(preset.filters, preset.sorts);
    toast.success(`Preset "${preset.name}" applied`);
  };

  const deletePreset = (presetId: string) => {
    setPresets(presets.filter(p => p.id !== presetId));
    toast.success("Preset deleted");
  };

  return (
    <div className="flex items-center gap-2">
      {presets.length > 0 && (
        <Select onValueChange={(value) => {
          const preset = presets.find(p => p.id === value);
          if (preset) applyPreset(preset);
        }}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Saved Presets" />
          </SelectTrigger>
          <SelectContent>
            {presets.map((preset) => (
              <SelectItem key={preset.id} value={preset.id}>
                <div className="flex items-center justify-between w-full">
                  <span>{preset.name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 ml-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      deletePreset(preset.id);
                    }}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <Popover open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" disabled={filters.length === 0 && sorts.length === 0}>
            <Save className="h-4 w-4 mr-2" />
            Save Preset
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80">
          <div className="space-y-4">
            <div>
              <h4 className="font-medium mb-2">Save Filter Preset</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Save current filters and sorting as a reusable preset
              </p>
            </div>
            <div className="space-y-2">
              <Label>Preset Name</Label>
              <Input
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                placeholder="e.g., High Priority Tasks"
              />
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                <strong>Filters:</strong> {filters.length} active
              </p>
              <p className="text-sm text-muted-foreground">
                <strong>Sorts:</strong> {sorts.length} active
              </p>
            </div>
            <Button onClick={savePreset} className="w-full">
              Save Preset
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {(filters.length > 0 || sorts.length > 0) && (
        <div className="flex items-center gap-1">
          {filters.length > 0 && (
            <Badge variant="secondary">
              {filters.length} filter{filters.length > 1 ? 's' : ''}
            </Badge>
          )}
          {sorts.length > 0 && (
            <Badge variant="secondary">
              {sorts.length} sort{sorts.length > 1 ? 's' : ''}
            </Badge>
          )}
        </div>
      )}
    </div>
  );
};